import { PencilSimpleLine } from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router-dom";

import RenderIf from "common/components/RenderIf";
import {
  ManageSubscriptionDialog,
  SubscriptionDetailsCard,
} from "common/components/subscriptions";
import { Button } from "common/components/ui/Button";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useCustomerEstimateRenewal from "common/datahooks/useCustomerEstimateRenewal";
import usePromotions from "common/datahooks/usePromotions";
import { formatPrice, formatPricingPeriodInfo } from "common/helpers/utils";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import NonRenewingTag from "../../workspace-settings/components/Billing/SubscriptionCards/NonRenewingTag";
import Price from "../../workspace-settings/components/Billing/SubscriptionCards/Price";
import ScheduledChangeTag from "../../workspace-settings/components/Billing/SubscriptionCards/Rented/ScheduledChangeTag";
import SubscriptionInfo from "../../workspace-settings/components/Billing/SubscriptionCards/SubscriptionInfo";
import ManageAgencySeats from "./ManageAgencySeats";
import SubscriptionCardSeatsSummary from "./SubscriptionCardSeatsSummary";

export default function AgencySubscriptionCard({
  isAgencyPage,
}: {
  isAgencyPage: boolean;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);
  const {
    id: workspaceId,
    agency_license_count,
    role,
  } = useSelectedWorkspaceContext();
  const { agencyLicensedAccounts } = useLicensedAccounts(workspaceId);
  const { agencyPlan } = usePromotions();

  const { subscription } = agencyPlan;

  const { estimateRenewal, isLoadingEstimateRenewal } =
    useCustomerEstimateRenewal(subscription);

  const {
    has_changes: hasChanges,
    status,
    item_price: itemPrice,
  } = subscription;

  let tagComponent =
    itemPrice.period === 6 &&
    itemPrice.id === "Agency-Plan-USD-Every-6-months" ? (
      <Tag variant="gradient">6 months deal</Tag>
    ) : (
      <Tag variant="success">Active</Tag>
    );

  if (status === "non_renewing") {
    tagComponent = <NonRenewingTag />;
  } else if (hasChanges) {
    tagComponent = <ScheduledChangeTag />;
  }

  let priceValue: string;
  let priceSubtext: string;
  if (subscription && estimateRenewal) {
    priceValue = formatPrice(estimateRenewal.amountDue / 100);
    priceSubtext = formatPricingPeriodInfo(subscription.item_price);
  }

  const priceComponent = isAgencyPage ? (
    <Price
      priceValue={priceValue}
      priceSubtext={priceSubtext}
      isLoading={isLoadingEstimateRenewal}
    />
  ) : (
    <RenderIf condition={role === "owner"}>
      <Button
        asChild
        size={isDesktop ? "sm" : "md"}
        intent="iconOnly"
        variant="outline-black"
      >
        <Link to="../agency">
          <PencilSimpleLine />
        </Link>
      </Button>
    </RenderIf>
  );

  const mainComponent = subscription && !isAgencyPage && (
    <SubscriptionCardSeatsSummary
      accounts={agencyLicensedAccounts}
      availableSeatsCount={agency_license_count}
      extraTagComponent={
        <Tag variant="tertiary" size="sm">
          {agencyPlan.unassigned_license_count} available
        </Tag>
      }
    />
  );

  const footerComponent = isAgencyPage ? (
    <SubscriptionInfo
      nextBillingAt={isAgencyPage && estimateRenewal?.nextBillingAt}
      isLoading={isLoadingEstimateRenewal}
      subscription={isAgencyPage && subscription}
      buttonComponent={
        <Button onClick={() => setIsOpen(true)} size={isDesktop ? "sm" : "md"}>
          Manage subscription
        </Button>
      }
    />
  ) : (
    <ManageAgencySeats />
  );

  return (
    <>
      <SubscriptionDetailsCard
        plan="agency"
        footerComponent={footerComponent}
        priceComponent={priceComponent}
        tagComponent={tagComponent}
        mainComponent={mainComponent}
      />
      <ManageSubscriptionDialog
        plan="agency"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </>
  );
}
